import { useState } from 'react';
import {
  ArchiveBoxIcon,
  CalendarIcon,
  CheckIcon,
  LockClosedIcon,
  LockOpenIcon,
  PlusCircleIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useToast } from '@/components/Toast';
import type { Poll } from '@flock-types/polls.types';
import type { UsePollDetailReturn } from '@/hooks/usePolls';
import PollExtendModal from './PollExtendModal';

interface Props {
  poll: Poll;
  detail: UsePollDetailReturn;
  onArchivedOrDeleted: () => void;
}

/** Leader Actions card (port of the Expo poll detail leader card). */
export default function PollLeaderActions({ poll, detail, onArchivedOrDeleted }: Props) {
  const { showToast } = useToast();
  const { addOption, closePoll, reopenPoll, updatePoll, deletePoll, isMutating } = detail;
  const [showAddOption, setShowAddOption] = useState(false);
  const [newOptionLabel, setNewOptionLabel] = useState('');
  const [showExtend, setShowExtend] = useState(false);

  const isClosed = poll.status === 'closed' || poll.status === 'archived';

  const handleAddOption = async () => {
    const label = newOptionLabel.trim();
    if (!label) return;
    try {
      await addOption(label);
      setNewOptionLabel('');
      setShowAddOption(false);
    } catch (e: any) {
      showToast(e?.response?.data?.message || 'Failed to add option. Please try again.', 'error');
    }
  };

  const handleClose = async () => {
    if (!window.confirm('Close this poll? No one will be able to vote after closing.')) return;
    try {
      await closePoll();
    } catch {
      showToast('Failed to close poll. Please try again.', 'error');
    }
  };

  const handleReopen = async () => {
    if (!window.confirm('Reopen this poll? Members will be able to vote again.')) return;
    try {
      await reopenPoll();
    } catch {
      showToast('Failed to reopen poll. Please try again.', 'error');
    }
  };

  const handleArchive = async () => {
    if (
      !window.confirm(
        'Archive this poll? This will hide the poll from members. You can still find it in the archived list.'
      )
    )
      return;
    try {
      await updatePoll({ status: 'archived' });
      onArchivedOrDeleted();
    } catch {
      showToast('Failed to archive poll. Please try again.', 'error');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this poll? This cannot be undone. All votes will be lost.')) return;
    try {
      await deletePoll();
      onArchivedOrDeleted();
    } catch {
      showToast('Failed to delete poll. Please try again.', 'error');
    }
  };

  const actionClass =
    'flex min-h-touch w-full items-center gap-2 rounded-lg px-2 py-2.5 text-sm font-medium transition hover:bg-flock-navy-50 disabled:opacity-50';

  return (
    <div className="rounded-xl border border-flock-gray-200 bg-white p-5">
      <h2 className="mb-2 text-base font-semibold text-flock-navy-700">Leader Actions</h2>

      {!isClosed &&
        (showAddOption ? (
          <div className="mb-2 flex items-center gap-2">
            <input
              autoFocus
              value={newOptionLabel}
              onChange={(e) => setNewOptionLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddOption();
              }}
              maxLength={255}
              placeholder="New option label"
              className="w-full flex-1 rounded-lg border border-flock-gray-200 bg-flock-navy-50 px-3 py-2 text-sm outline-none focus:border-flock-navy-400"
            />
            <button
              onClick={handleAddOption}
              disabled={!newOptionLabel.trim() || isMutating}
              className="rounded-lg bg-flock-navy-600 p-2.5 text-white transition hover:bg-flock-navy-700 disabled:opacity-50"
              title="Add option"
            >
              <CheckIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => {
                setShowAddOption(false);
                setNewOptionLabel('');
              }}
              className="rounded-lg p-2.5 text-flock-navy-400 transition hover:bg-flock-navy-50"
              title="Cancel"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        ) : (
          <button onClick={() => setShowAddOption(true)} className={`${actionClass} text-flock-navy-600`}>
            <PlusCircleIcon className="h-5 w-5" />
            Add Option
          </button>
        ))}

      {!isClosed && (
        <button onClick={() => setShowExtend(true)} className={`${actionClass} text-flock-navy-600`}>
          <CalendarIcon className="h-5 w-5" />
          Extend Poll
        </button>
      )}

      {!isClosed ? (
        <button onClick={handleClose} disabled={isMutating} className={`${actionClass} text-amber-600`}>
          <LockClosedIcon className="h-5 w-5" />
          Close Poll
        </button>
      ) : (
        <button onClick={handleReopen} disabled={isMutating} className={`${actionClass} text-green-600`}>
          <LockOpenIcon className="h-5 w-5" />
          Reopen Poll
        </button>
      )}

      {poll.status !== 'archived' && (
        <button
          onClick={handleArchive}
          disabled={isMutating}
          className={`${actionClass} text-flock-navy-500`}
        >
          <ArchiveBoxIcon className="h-5 w-5" />
          Archive Poll
        </button>
      )}

      <button onClick={handleDelete} disabled={isMutating} className={`${actionClass} text-red-600`}>
        <TrashIcon className="h-5 w-5" />
        Delete Poll
      </button>

      {showExtend && (
        <PollExtendModal
          poll={poll}
          extendPoll={detail.extendPoll}
          isMutating={isMutating}
          onClose={() => setShowExtend(false)}
        />
      )}
    </div>
  );
}
